import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { getMyReport, saveMyReport } from "@/lib/coach-report.functions";
import {
  reportFormSchema,
  TEXT_MAX,
  TRL_COMMENT_MAX,
  HOURS_MAX,
} from "@/lib/coach-report.schema";
import { MONTH_NAMES_SV, TRL_LABELS, reportingPeriod } from "@/lib/monthly-report";

const searchSchema = z.object({
  year: z.number().int().optional(),
  month: z.number().int().min(1).max(12).optional(),
});

export const Route = createFileRoute("/coach-portal/$companyId/rapport")({
  validateSearch: (s) => searchSchema.parse(s),
  component: CoachReport,
});

type FormState = {
  id?: string;
  crl: number | null;
  brl: number | null;
  tmrl: number | null;
  srl: number | null;
  trl_comment: string;
  progress: string;
  obstacles: string;
  next_milestones: string;
  support_needed: string;
  hours_logged: number | null;
  summary: string;
};

const empty: FormState = {
  crl: null, brl: null, tmrl: null, srl: null,
  trl_comment: "", progress: "", obstacles: "", next_milestones: "",
  support_needed: "", hours_logged: null, summary: "",
};

function CoachReport() {
  const { companyId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const period = useMemo(() => {
    const def = reportingPeriod();
    return { year: search.year ?? def.year, month: search.month ?? def.month };
  }, [search.year, search.month]);

  const fetchReport = useServerFn(getMyReport);
  const save = useServerFn(saveMyReport);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["coach-report", companyId, period.year, period.month],
    queryFn: () =>
      fetchReport({ data: { company_id: companyId, year: period.year, month: period.month } }),
  });

  const [form, setForm] = useState<FormState>(empty);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [autoStatus, setAutoStatus] = useState<"idle" | "dirty" | "saving" | "saved" | "error">("idle");
  const lastSavedSnapshotRef = useRef<string | null>(null);
  const lastSavedFormRef = useRef<FormState | null>(null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!data) return;
    hydratedRef.current = false;
    let initial: FormState;
    if (data.report) {
      const r = data.report;
      initial = {
        id: r.id,
        crl: r.crl, brl: r.brl, tmrl: r.tmrl, srl: r.srl,
        trl_comment: r.trl_comment ?? "",
        progress: r.progress ?? "",
        obstacles: r.obstacles ?? "",
        next_milestones: r.next_milestones ?? "",
        support_needed: r.support_needed ?? "",
        hours_logged: r.hours_logged,
        summary: r.summary ?? "",
      };
    } else {
      initial = empty;
    }
    setForm(initial);
    setErrors({});
    setAutoStatus("idle");
    lastSavedSnapshotRef.current = data.report ? JSON.stringify(initial) : null;
    lastSavedFormRef.current = data.report ? initial : null;
    queueMicrotask(() => { hydratedRef.current = true; });
  }, [data]);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => {
      if (!e[k as string]) return e;
      const { [k as string]: _, ...rest } = e;
      return rest;
    });
  };

  const buildPayload = useCallback(() => {
    return {
      id: form.id,
      company_id: companyId,
      year: period.year,
      month: period.month,
      crl: form.crl, brl: form.brl, tmrl: form.tmrl, srl: form.srl,
      trl_comment: form.trl_comment.trim() || null,
      progress: form.progress.trim() || null,
      obstacles: form.obstacles.trim() || null,
      next_milestones: form.next_milestones.trim() || null,
      support_needed: form.support_needed.trim() || null,
      hours_logged: form.hours_logged,
      summary: form.summary.trim() || null,
    };
  }, [form, companyId, period.year, period.month]);

  const liveValidation = reportFormSchema.safeParse(buildPayload());
  const isValid = liveValidation.success;

  const persist = useCallback(
    async (silent: boolean) => {
      const parsed = reportFormSchema.safeParse(buildPayload());
      if (!parsed.success) {
        if (!silent) {
          const fieldErrors: Record<string, string> = {};
          for (const issue of parsed.error.issues) {
            const key = issue.path[0]?.toString() ?? "_";
            if (!fieldErrors[key]) fieldErrors[key] = issue.message;
          }
          setErrors(fieldErrors);
          toast.error("Vänligen åtgärda fälten innan du sparar");
        }
        return false;
      }
      setErrors({});
      const snapshot = JSON.stringify(parsed.data);
      if (snapshot === lastSavedSnapshotRef.current) return true;
      if (silent) setAutoStatus("saving"); else setSaving(true);
      try {
        const res = await save({ data: parsed.data });
        lastSavedSnapshotRef.current = snapshot;
        setForm((f) => {
          const next = f.id === res.id ? f : { ...f, id: res.id };
          lastSavedFormRef.current = next;
          return next;
        });
        const stamp = new Date().toLocaleTimeString("sv-SE");
        setSavedAt(stamp);
        if (silent) setAutoStatus("saved"); else toast.success("Rapport sparad");
        return true;
      } catch (e) {
        if (silent) setAutoStatus("error");
        else toast.error(e instanceof Error ? e.message : "Kunde inte spara.");
        return false;
      } finally {
        if (!silent) setSaving(false);
      }
    },
    [buildPayload, save],
  );

  // Debounced autosave: kick off 1.2s after last change, only when valid + hydrated
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (!isValid) return;
    const snapshot = JSON.stringify(buildPayload());
    if (snapshot === lastSavedSnapshotRef.current) return;
    setAutoStatus("dirty");
    const handle = setTimeout(() => { void persist(true); }, 1200);
    return () => clearTimeout(handle);
  }, [form, isValid, buildPayload, persist]);

  // Flush pending changes on unmount / page hide
  useEffect(() => {
    const flush = () => {
      if (!hydratedRef.current) return;
      const snapshot = JSON.stringify(buildPayload());
      if (snapshot !== lastSavedSnapshotRef.current && isValid) {
        void persist(true);
      }
    };
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      flush();
    };
  }, [buildPayload, isValid, persist]);

  async function handleSave() {
    await persist(false);
  }

  const canRevert =
    lastSavedFormRef.current != null &&
    JSON.stringify(buildPayload()) !== lastSavedSnapshotRef.current;

  function handleRevert() {
    const snap = lastSavedFormRef.current;
    if (!snap) return;
    setForm(snap);
    setErrors({});
    setAutoStatus("idle");
    toast.success("Återställde till senast autosparade version");
  }

  if (isLoading) return <div className="p-8 text-sm text-muted-foreground">Laddar…</div>;
  if (error) return <div className="p-8 text-sm text-destructive">{(error as Error).message}</div>;
  if (!data) return null;

  const periodOptions: { y: number; m: number }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    periodOptions.push({ y: d.getFullYear(), m: d.getMonth() + 1 });
  }

  return (
    <div className="px-6 md:px-10 py-10 max-w-3xl">
      <Link
        to="/coach-portal/$companyId"
        params={{ companyId }}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="size-3.5" /> Tillbaka till {data.company.name}
      </Link>

      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
            Månadsrapport
          </div>
          <h1 className="font-display text-3xl">{data.company.name}</h1>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Period</Label>
          <select
            value={`${period.year}-${period.month}`}
            onChange={(e) => {
              const [y, m] = e.target.value.split("-").map(Number);
              navigate({
                to: "/coach-portal/$companyId/rapport",
                params: { companyId },
                search: { year: y, month: m },
              });
            }}
            className="mt-1 h-10 px-3 rounded-lg bg-secondary text-sm"
          >
            {periodOptions.map((p) => (
              <option key={`${p.y}-${p.m}`} value={`${p.y}-${p.m}`}>
                {MONTH_NAMES_SV[p.m - 1]} {p.y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <section className="rounded-2xl border border-border bg-card p-6 mb-6">
        <h2 className="font-display text-lg mb-1">TRL-mognad</h2>
        <p className="text-xs text-muted-foreground mb-4">
          Välj nivå {1}–{9} för respektive dimension. Lämna tomt om okänd.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {(["crl", "brl", "tmrl", "srl"] as const).map((k) => (
            <label key={k} className="block">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                {TRL_LABELS[k]}
              </span>
              <select
                value={form[k] ?? ""}
                onChange={(e) =>
                  update(k, e.target.value === "" ? null : Number(e.target.value))
                }
                className={`mt-1 w-full h-10 px-3 rounded-lg bg-secondary text-sm ${errors[k] ? "ring-1 ring-destructive" : ""}`}
                aria-invalid={errors[k] ? true : undefined}
              >
                <option value="">— välj nivå (1–9) —</option>
                {Array.from({ length: 9 }).map((_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}</option>
                ))}
              </select>
              {errors[k] && <p className="mt-1 text-xs text-destructive">{errors[k]}</p>}
            </label>
          ))}
        </div>
        <Field
          label="Kommentar TRL (valfritt)"
          error={errors.trl_comment}
          hint={`${form.trl_comment.length} / ${TRL_COMMENT_MAX}`}
        >
          <Textarea
            rows={3}
            maxLength={TRL_COMMENT_MAX}
            value={form.trl_comment}
            onChange={(e) => update("trl_comment", e.target.value)}
          />
        </Field>
      </section>

      <section className="rounded-2xl border border-border bg-card p-6 mb-6 space-y-4">
        <h2 className="font-display text-lg">Månadens innehåll</h2>
        <Field label="Framsteg" error={errors.progress} hint={`${form.progress.length} / ${TEXT_MAX}`}>
          <Textarea rows={3} maxLength={TEXT_MAX} value={form.progress} onChange={(e) => update("progress", e.target.value)}
            placeholder="Vad har bolaget åstadkommit denna månad?" />
        </Field>
        <Field label="Hinder & utmaningar" error={errors.obstacles} hint={`${form.obstacles.length} / ${TEXT_MAX}`}>
          <Textarea rows={3} maxLength={TEXT_MAX} value={form.obstacles} onChange={(e) => update("obstacles", e.target.value)} />
        </Field>
        <Field label="Milstolpar nästa månad" error={errors.next_milestones} hint={`${form.next_milestones.length} / ${TEXT_MAX}`}>
          <Textarea rows={3} maxLength={TEXT_MAX} value={form.next_milestones} onChange={(e) => update("next_milestones", e.target.value)} />
        </Field>
        <Field label="Behov av stöd från Inkubera" error={errors.support_needed} hint={`${form.support_needed.length} / ${TEXT_MAX}`}>
          <Textarea rows={3} maxLength={TEXT_MAX} value={form.support_needed} onChange={(e) => update("support_needed", e.target.value)} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nedlagda timmar" error={errors.hours_logged}>
            <Input
              type="number" min={0} max={HOURS_MAX} step={0.5}
              value={form.hours_logged ?? ""}
              aria-invalid={errors.hours_logged ? true : undefined}
              onChange={(e) =>
                update("hours_logged", e.target.value === "" ? null : Number(e.target.value))
              }
            />
          </Field>
        </div>
        <Field label="Sammanfattning" error={errors.summary} hint={`${form.summary.length} / ${TEXT_MAX}`}>
          <Textarea rows={3} maxLength={TEXT_MAX} value={form.summary} onChange={(e) => update("summary", e.target.value)}
            placeholder="Två-tre meningar som sammanfattar månaden" />
        </Field>
      </section>

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={saving || !isValid}>
          <Save className="size-4 mr-2" />
          {saving ? "Sparar…" : form.id ? "Uppdatera rapport" : "Spara rapport"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleRevert}
          disabled={saving || !canRevert}
          title={canRevert ? "Återställ till senast autosparade version" : "Inget autosparat att återställa till"}
        >
          <RotateCcw className="size-4 mr-2" />
          Återställ
        </Button>
        {!isValid && (
          <span className="text-xs text-muted-foreground">
            Fyll i minst en TRL-nivå eller framsteg/sammanfattning
          </span>
        )}
        <span className="text-xs text-muted-foreground" aria-live="polite">
          {autoStatus === "saving" && "Sparar automatiskt…"}
          {autoStatus === "dirty" && "Osparade ändringar…"}
          {autoStatus === "error" && "Autospar misslyckades – försöker igen vid nästa ändring"}
          {(autoStatus === "saved" || (autoStatus === "idle" && savedAt)) && savedAt && `Autosparat ${savedAt}`}
        </span>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  error,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  hint?: string;
}) {
  return (
    <label className="block">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        {hint && <span className="text-[10px] text-muted-foreground tabular-nums">{hint}</span>}
      </div>
      <div className="mt-1">{children}</div>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </label>
  );
}
